import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { Photo, PhotoInput } from '../types/Photo';

const PHOTOS_COLLECTION = 'photos';

// Convert Firestore timestamp to Date
const convertTimestamp = (timestamp: any): Date => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  return timestamp;
};

// Get all photos
export const getAllPhotos = async (): Promise<Photo[]> => {
  try {
    // Just get all photos without ordering
    const querySnapshot = await getDocs(collection(db, PHOTOS_COLLECTION));

    const photos = querySnapshot.docs.map((doc, index) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        order: data.order ?? index,
        date: convertTimestamp(data.date),
        createdAt: data.createdAt ? convertTimestamp(data.createdAt) : undefined,
        updatedAt: data.updatedAt ? convertTimestamp(data.updatedAt) : undefined,
      } as Photo;
    });

    // Sort by order field if it exists, otherwise by date
    return photos.sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      // Fallback to date sorting
      const dateA = typeof a.date === 'string' ? new Date(a.date) : a.date;
      const dateB = typeof b.date === 'string' ? new Date(b.date) : b.date;
      return dateB.getTime() - dateA.getTime();
    });
  } catch (error) {
    console.error('Error fetching photos:', error);
    throw error;
  }
};

// Get a single photo by ID
export const getPhotoById = async (id: string): Promise<Photo | null> => {
  try {
    const docRef = doc(db, PHOTOS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        order: data.order ?? 0,
        date: convertTimestamp(data.date),
        createdAt: data.createdAt ? convertTimestamp(data.createdAt) : undefined,
        updatedAt: data.updatedAt ? convertTimestamp(data.updatedAt) : undefined,
      } as Photo;
    }

    return null;
  } catch (error) {
    console.error('Error fetching photo:', error);
    throw error;
  }
};

// Add a new photo
export const addPhoto = async (photoData: PhotoInput): Promise<string> => {
  try {
    // Get current max order
    const photos = await getAllPhotos();
    const maxOrder = photos.length > 0 ? Math.max(...photos.map(p => p.order ?? 0)) : -1;

    const docRef = await addDoc(collection(db, PHOTOS_COLLECTION), {
      ...photoData,
      order: maxOrder + 1,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    return docRef.id;
  } catch (error) {
    console.error('Error adding photo:', error);
    throw error;
  }
};

// Update a photo
export const updatePhoto = async (id: string, photoData: Partial<PhotoInput>): Promise<void> => {
  try {
    const docRef = doc(db, PHOTOS_COLLECTION, id);
    await updateDoc(docRef, {
      ...photoData,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating photo:', error);
    throw error;
  }
};

// Delete a photo
export const deletePhoto = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, PHOTOS_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting photo:', error);
    throw error;
  }
};

// Upload image to Firebase Storage
export const uploadImage = async (file: File, path: string = 'photos'): Promise<string> => {
  try {
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storageRef = ref(storage, `${path}/${fileName}`);

    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

// Delete image from Firebase Storage
export const deleteImage = async (imageURL: string): Promise<void> => {
  try {
    const imageRef = ref(storage, imageURL);
    await deleteObject(imageRef);
  } catch (error) {
    console.error('Error deleting image:', error);
    throw error;
  }
};

// Update photo orders
export const updatePhotoOrders = async (photoOrders: { id: string; order: number }[]): Promise<void> => {
  try {
    const updatePromises = photoOrders.map(({ id, order }) => {
      const docRef = doc(db, PHOTOS_COLLECTION, id);
      return updateDoc(docRef, {
        order,
        updatedAt: Timestamp.now(),
      });
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error updating photo orders:', error);
    throw error;
  }
};

// Initialize order field for existing photos (migration utility)
export const initializePhotoOrders = async (): Promise<void> => {
  try {
    const photosQuery = query(
      collection(db, PHOTOS_COLLECTION),
      orderBy('date', 'desc')
    );
    const querySnapshot = await getDocs(photosQuery);

    const updatePromises = querySnapshot.docs.map((docSnapshot, index) => {
      const docRef = doc(db, PHOTOS_COLLECTION, docSnapshot.id);
      return updateDoc(docRef, {
        order: index,
        updatedAt: Timestamp.now(),
      });
    });

    await Promise.all(updatePromises);
    console.log('Successfully initialized order fields for all photos');
  } catch (error) {
    console.error('Error initializing photo orders:', error);
    throw error;
  }
};
